import { createAuth0Client } from "@auth0/auth0-spa-js";
import {
  isReauthError,
  hasCallbackParams,
  urlWithoutCallbackParams,
  takeCallbackRetry,
  clearCallbackRetry,
} from "@bitcashier/shared/auth/reauth.js";
import { env } from "../core/env.js";
import { deleteActiveSentryUser, reportError } from "../observability/sentry.js";

// Replaces the React admin's auth stack: user/auth0_provider_with_history (Auth0Provider config),
// shared/api/security.js (the `sec` token/login indirection), and shared/utils ProtectedComponent
// (the redirect-to-login guard). One reactive singleton the whole app reads.

let client = null;

export const auth = $state({
  loading: true,
  isAuthenticated: false,
  user: null,
  error: null,
});

// Handles first-load: constructs the client, completes an Auth0 redirect callback if present,
// and resolves the current session. Called once from App before rendering routes.
export async function initAuth() {
  try {
    client = await createAuth0Client({
      domain: env.auth0Domain,
      clientId: env.auth0ClientId,
      authorizationParams: {
        redirect_uri: env.appUrl,
        audience: env.auth0Audience,
        scope: "openid profile email",
      },
      useRefreshTokens: true,
      cacheLocation: "localstorage",
    });

    // A callback that did not produce a session leaves the browser somewhere else entirely (back
    // at Auth0, or on the sign-in-failed screen), so there is no session left to resolve here.
    if (hasCallbackParams(window.location.search) && !(await completeRedirectCallback())) return;

    auth.isAuthenticated = (await client.isAuthenticated()) && (await tokenIsObtainable());
    auth.user = auth.isAuthenticated ? await client.getUser() : null;
    auth.loading = false;
  } catch (e) {
    // App.svelte calls this from onMount(initAuth), which does not await the returned promise, so
    // anything escaping here (createAuth0Client's discovery-document fetch, isAuthenticated(),
    // getUser() -- none of which completeRedirectCallback/tokenIsObtainable already catch) became an
    // unhandled promise rejection at the root route instead of a reported boot failure
    // (DASHBOARD-C7/C6). Same shape as loadClients/loadPermissions: report it and stop loading
    // rather than leaving auth.loading true forever with nothing on screen saying why.
    reportError(e, { boot: "auth" });
    auth.error = e;
    auth.isAuthenticated = false;
    auth.user = null;
    auth.loading = false;
  }
}

// Returns whether the app should carry on booting.
async function completeRedirectCallback() {
  try {
    const { appState } = await client.handleRedirectCallback();
    clearCallbackRetry(window.sessionStorage);
    window.history.replaceState({}, document.title, appState?.returnTo || "/");
    return true;
  } catch (e) {
    // The authorization code is single-use and short-lived, so this is reached by ordinary means: a
    // tab restored by the browser replays a code that expired overnight, a reload repeats one
    // already spent, the POST to /oauth/token never gets an answer, or Auth0 refused the login
    // outright. The failure used to be written to `auth.error` and read by nobody — the URL kept
    // its dead code (so every reload repeated the failure), and the boot went on to ask
    // `isAuthenticated()`, which answers from the still-cached user entry and says yes. The app
    // then rendered as a signed-in user with no token, and the first API call threw.
    const returnTo = urlWithoutCallbackParams(window.location.pathname, window.location.search);
    window.history.replaceState({}, document.title, returnTo);

    if (takeCallbackRetry(window.sessionStorage)) {
      login(returnTo);
      return false;
    }

    // A second failure in the same tab is not a blip. Stop, and let App say so: asking Auth0 again
    // would be a loop nobody can see, since the SSO session answers instantly.
    auth.error = e;
    auth.isAuthenticated = false;
    auth.user = null;
    auth.loading = false;
    return false;
  }
}

// `isAuthenticated()` reports only that a user entry is cached, and that entry is a different
// localStorage key from the tokens — it outlives them. Booting on it alone is what put a signed-in
// user in front of a dashboard whose every read failed with `missing_refresh_token`, so the session
// is only accepted if a token can actually be had for it (a cache hit, and no round trip, in the
// ordinary case). A blip is not an answer about the session: only Auth0 saying the silent session
// is gone counts, and anything else boots as before and leaves it to the reads, which retry.
async function tokenIsObtainable() {
  try {
    await client.getTokenSilently({ authorizationParams: { audience: env.auth0Audience, scope: "openid" } });
    return true;
  } catch (e) {
    return !isReauthError(e);
  }
}

let redirectInFlight = null;

// `forcePrompt` maps to Auth0's `prompt: "login"`, which the React /login route always sent: it
// makes Auth0 ask for credentials again instead of silently resuming an existing SSO session, so a
// user can sign in as someone else. The guard redirect deliberately does NOT force it — that would
// re-prompt a perfectly valid session on every deep link.
export function login(returnTo = window.location.pathname, { forcePrompt = false } = {}) {
  // The "sign in failed" screen's retry button reaches this too, and it is reachable from a boot
  // where createAuth0Client() itself never succeeded (initAuth's catch sets auth.error without a
  // client to retry with). A full reload re-runs initAuth from scratch rather than throwing on
  // client.loginWithRedirect of a null client.
  if (!client) {
    window.location.reload();
    return Promise.resolve();
  }

  // Two callers can decide to re-authenticate in the same instant: getToken() on a dead session,
  // and App's guard the moment that flips `isAuthenticated` to false. loginWithRedirect stores its
  // state and PKCE verifier under one key per client, so a second call overwrites the first's
  // transaction — and the browser then navigates with whichever `state` won the race, which comes
  // back as a callback Auth0's own SDK rejects. One redirect at a time.
  if (redirectInFlight) return redirectInFlight;
  redirectInFlight = client
    .loginWithRedirect({
      appState: { returnTo },
      ...(forcePrompt ? { authorizationParams: { prompt: "login" } } : {}),
    })
    .catch((e) => {
      redirectInFlight = null;
      // Nothing awaits or catches this: the auth guard effect, getToken()'s reauth kick on a dead
      // session, a replayed callback retry, and the retry button all call login() and move on --
      // ordinarily fine, since a successful redirect tears the page down anyway. A failure (a blocked
      // storage write, WebCrypto refused outside a secure context, the token request itself failing
      // over a bad connection) had nowhere to go and surfaced as an unhandled promise rejection
      // wherever the user happened to be -- Sentry saw it as a generic "UnhandledRejection" on
      // payment-links and as "TypeError: Failed to fetch" on /ledger, both call sites of this same
      // promise, just rejected with a different underlying failure. Same shape as initAuth's own
      // catch: report it and land on the existing sign-in-failed screen instead of leaving the loader
      // spinning with nothing on screen saying why.
      reportError(e, { boot: "login" });
      auth.error = e;
      auth.isAuthenticated = false;
      auth.user = null;
      auth.loading = false;
    });
  return redirectInFlight;
}

export function logout() {
  // Stop attributing anything that happens after sign-out to the client that was active (React did
  // this from layout.jsx's unmount cleanup).
  deleteActiveSentryUser();
  return client.logout({ logoutParams: { returnTo: env.appUrl } });
}

// The token accessor the API client uses per request. On a hard re-auth error it kicks off an
// interactive login (as the React transport did) and rethrows so the caller can stop.
export async function getToken() {
  try {
    return await client.getTokenSilently({
      authorizationParams: { audience: env.auth0Audience, scope: "openid" },
    });
  } catch (e) {
    if (isReauthError(e)) {
      auth.isAuthenticated = false;
      login();
    }
    throw e;
  }
}

// The OpenID PAPE policy URI for "multi-factor". Asking for it is how the dashboard tells the Auth0 post-login Action
// that this login is a step-up: the Action then demands the second factor even on a live SSO session, and stamps the
// time it was completed into the access token, which is what the backend checks (MfaReverificationGate in core).
export const MFA_ACR = "http://schemas.openid.net/pape/policies/2007/06/multi-factor";

// How long the step-up popup may take. auth0-spa-js gives up after 60s by default, and then only stops listening: the
// popup stays open, and a second factor completed in it after that (an SMS or push that took its time) is thrown away
// and the action fails with "Timeout". It must stay under the backend's 5-minute window (MfaReverificationGate.MaxAge in
// core), since the Action stamps the time the step-up started and the retry has to land inside that window.
export const MFA_POPUP_TIMEOUT_SECONDS = 240;

let reverifyInFlight = null;

// Runs the 2FA step-up for a client that requires it before money moves. Resolves once a fresh token is cached, so the
// caller can simply retry its request: getToken() answers from the same cache entry (same audience and scope). A popup
// keeps the user on the page with whatever they had typed; a browser that blocks it is sent through a redirect instead,
// which lands them back on this page to repeat the action. One prompt at a time, however many requests were refused.
export function reverifyMfa() {
  if (reverifyInFlight) return reverifyInFlight;
  const authorizationParams = { audience: env.auth0Audience, scope: "openid", acr_values: MFA_ACR };
  reverifyInFlight = client
    .getTokenWithPopup({ authorizationParams }, { timeoutInSeconds: MFA_POPUP_TIMEOUT_SECONDS })
    .catch((e) => {
      if (e?.error === "popup_open") {
        // The page is about to navigate away; never settling keeps the caller from retrying into a second refusal.
        return client
          .loginWithRedirect({
            appState: { returnTo: window.location.pathname + window.location.search },
            authorizationParams,
          })
          .then(() => new Promise(() => {}));
      }
      // On timeout auth0-spa-js stops listening but leaves the popup open. A factor completed in it after that would
      // look like it worked while the action had already failed, so close it: the user retries into a fresh prompt.
      if (e?.error === "timeout") e.popup?.close?.();
      throw e;
    })
    .finally(() => {
      reverifyInFlight = null;
    });
  return reverifyInFlight;
}
